import { Alert, Heading, Link as CLink, SimpleGrid, Stack, Text } from '@chakra-ui/react'
import type { ElementType } from 'react'
import { LuDatabase, LuKeyRound, LuMessageSquare, LuUser } from 'react-icons/lu'
import { Link as RouterLink, Navigate } from 'react-router'
import { isApiError } from '~/api/client'
import { useJoinById, useMe, useSignInSettings } from '~/api/hooks'
import { RetryError } from '~/components/member/feedback'
import { MemberSection } from '~/components/member/layout'
import { IconTile, type DecoHue } from '~/components/IconTile'
import { useT } from '~/i18n/use-t'
import { StatusCard } from './StatusCard'
import { StepSkeleton } from '~/components/member/StepSkeleton'

const CARDS: Array<{ to: string; key: string; icon: ElementType; hue: DecoHue }> = [
  { to: '/me/profile', key: 'profile', icon: LuUser, hue: 'green' },
  { to: '/me/contact', key: 'contact', icon: LuMessageSquare, hue: 'blue' },
  { to: '/me/sign-in', key: 'signIn', icon: LuKeyRound, hue: 'peach' },
  { to: '/me/data', key: 'data', icon: LuDatabase, hue: 'green' },
]

/**
 * Passwordless P10: a voter who signs in with a password learns that the sign-in email works too. Only for
 * `password_or_email` (a `password_and_email` account still needs the password and the code).
 */
function EmailSignInNotice() {
  const { t } = useT()
  const settings = useSignInSettings()
  if (settings.data?.mode !== 'password_or_email') return null
  return (
    <Alert.Root status='info' borderRadius='md' maxW='3xl'>
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Description>
          {t('dashboard.emailSignInNotice')}{' '}
          <CLink asChild textDecoration='underline'>
            <RouterLink to='/me/sign-in'>{t('dashboard.emailSignInNoticeLink')}</RouterLink>
          </CLink>
        </Alert.Description>
      </Alert.Content>
    </Alert.Root>
  )
}

/**
 * `/me`: the status card, and for a member who is in (approved, or waiting for a person's look) a short welcome with
 * quick cards to the sections. A member with a "not accepted" or "do the face check again" card sees the card only.
 */
export default function Overview() {
  const { t } = useT()
  const me = useMe()
  const join = useJoinById(me.data?.providerId).data

  if (me.isPending) return <StepSkeleton />
  if (me.isError) {
    if (isApiError(me.error) && me.error.status === 401) return <Navigate to='/login' replace />
    return <RetryError error={me.error} onRetry={() => void me.refetch()} />
  }
  const { card, member } = me.data
  const first = member?.name?.trim()
  return (
    <>
      <Stack maxW='3xl'>
        <StatusCard
          card={card}
          name={member?.name}
          reasonCategories={me.data.reasonCategories}
          rejectReasonCategory={me.data.rejectReasonCategory}
          providerName={member?.provider}
          providerKeepsPhotos={join?.faceNotice === 'kept_for_review'}
          documentVerified={me.data.documentVerified === true}
          retryTo='/me/face'
          deleteTo='/me/delete'
          writeToUsTo={member ? '/me/contact' : undefined}
        />
      </Stack>
      {member && <EmailSignInNotice />}
      {member && (
        <Stack gap={4}>
          <Heading as='h2' size='xl'>
            {first ? t('memberNav.quickTitle', { first }) : t('memberNav.quickTitleNoName')}
          </Heading>
          <SimpleGrid columns={{ base: 1, sm: 2 }} gap={4} maxW='3xl'>
            {CARDS.map(({ to, key, icon, hue }) => (
              <MemberSection
                key={key}
                asChild
                p={{ base: 3, sm: 4 }}
                _hover={{ bg: 'bg.muted' }}
                _focusVisible={{ outline: '2px solid', outlineColor: 'brand.solid', outlineOffset: '2px' }}
              >
                <RouterLink to={to}>
                  <Stack direction='row' gap={4} align='center'>
                    <IconTile icon={icon} hue={hue} size='lg' />
                    <Stack gap={0.5} minW={0}>
                      <Text fontWeight='medium'>{t(`memberNav.${key}`)}</Text>
                      <Text fontSize='sm' color='fg.muted'>
                        {t(`memberNav.${key}Hint`)}
                      </Text>
                    </Stack>
                  </Stack>
                </RouterLink>
              </MemberSection>
            ))}
          </SimpleGrid>
        </Stack>
      )}
    </>
  )
}
